import { h, Component, ComponentType } from "preact";

import CSSProperties from "../../foundation/model/CSSProperties";
import BasicRecord from "../../foundation/utils/BasicRecord";
import noderef from "../../foundation/utils/noderef";
import { rectContainsPoint } from "../../foundation/utils/rectangle";

import { DragDropContainerState, DropTargetItem, DragPosition } from "./types";

interface DropTargetOptions<P, T = P> {
    acceptsItemType?: string | string[] | Record<string, string | string[]>;
    discardHorizontalPosition?: boolean;
    discardVerticalPosition?: boolean;
    endsInclusive?: boolean;
    style?: CSSProperties;
    onHover?: (props: P, dragState: DragDropContainerState<T>) => void;
    onHoverOut?: (props: P, dragState: DragDropContainerState<T>) => void;
    onDrop?: (props: P, dragState: DragDropContainerState<T>, continuePropagation?: () => void) => void;
}

/**
 * P: Props of the wrapped component
 * T: Type of the drag data
 */
function DropTarget<P, T = P>(
    options: DropTargetOptions<P, T>
): (WrappedComponent: ComponentType<P>) => ComponentType<P> {
    return (WrappedComponent: ComponentType<P>) => class extends Component<P> implements DropTargetItem<T> {
        nodeRefs: BasicRecord<HTMLElement | null> = {};
        hasHover = false;

        private acceptsItemType = (itemType: string, actionType: "drag" | "drop") => {
            if (typeof options.acceptsItemType === "string") {
                return options.acceptsItemType === itemType;
            }

            if (Array.isArray(options.acceptsItemType)) {
                return options.acceptsItemType.includes(itemType);
            }

            if (typeof options.acceptsItemType === "object") {
                const actionAcceptItemType = options.acceptsItemType[actionType];
                if (typeof actionAcceptItemType === "string") {
                    return actionAcceptItemType === itemType;
                }

                if (Array.isArray(actionAcceptItemType)) {
                    return actionAcceptItemType.includes(itemType);
                }
            }
        };

        public checkHoverOut = (dragState: DragDropContainerState<T>) => {
            if (!this.acceptsItemType(dragState.draggingItemType, "drag")) {
                return;
            }

            if (this.hasHover) {
                if (options.onHoverOut) {
                    options.onHoverOut(this.props, dragState);
                }

                this.hasHover = false;
            }
        };

        // Checks drop and returns whether item is dropped or not
        public checkDrop = (mirrorPosition: DragPosition, dragState: DragDropContainerState<T>) => {
            if (!this.acceptsItemType(dragState.draggingItemType, "drop")) {
                return false;
            }

            /**
             * Whether to stop looking for another drop candidate after one drop target contains the position
             * Initially, it is true; so, it stops looking for another drop candidate and handles drop event only once.
             * However, if a DropTarget calls `continuePropagation` in its drop handler;
             * it continues to look for another drop candidate to make it handle drop event too.
             */
            let stopPropagation = true;
            const continuePropagation = () => {
                stopPropagation = false;
            };
            const rect = this.nodeRefs["base"]!.getBoundingClientRect();
            const pointerOrigin = { x: mirrorPosition.left, y: mirrorPosition.top };
            const { discardHorizontalPosition, discardVerticalPosition, endsInclusive } = options;
            if (
                rectContainsPoint(rect, pointerOrigin, {
                    discardHorizontalPosition,
                    discardVerticalPosition,
                    endsInclusive
                })
            ) {
                if (options.onDrop) {
                    options.onDrop(this.props, dragState, continuePropagation);
                }

                return stopPropagation;
            }

            return false;
        };

        public checkHover = (mirrorPosition: DragPosition, dragState: DragDropContainerState<T>) => {
            if (!this.acceptsItemType(dragState.draggingItemType, "drag")) {
                return;
            }

            const rect = this.nodeRefs["base"]!.getBoundingClientRect();
            const pointerOrigin = { x: mirrorPosition.left, y: mirrorPosition.top };
            const { discardHorizontalPosition, discardVerticalPosition, endsInclusive } = options;
            if (
                rectContainsPoint(rect, pointerOrigin, {
                    discardHorizontalPosition,
                    discardVerticalPosition,
                    endsInclusive
                })
            ) {
                if (options.onHover) {
                    options.onHover(this.props, dragState);
                }

                if (!this.hasHover) {
                    this.hasHover = true;
                }

                return;
            }

            this.checkHoverOut(dragState);
        };

        override componentDidMount() {
            this.context.registerDropTarget(this);
        }

        override componentWillUnmount() {
            this.context.unregisterDropTarget(this);
        }

        render() {
            return (
                <div style={options.style} ref={noderef(this, "base")}>
                    <WrappedComponent {...this.props}/>
                </div>
            );
        }
    };
}

/** @component */
export default DropTarget;
